import styled from "styled-components";
import {Button, IconButton, Typography} from "@mui/material";
import {dismissRowInlinePadding, radii, SMALL_ICON_BUTTON_PADDING_PX} from "theme/tokens";
import {goldClaimFill, goldOfferWash} from "./offerSurface.styles";

/**
 * The callout's own inline padding — the distance every child sits from its edge, the dismiss X
 * included. The right number is smaller because that button carries MUI's own padding inside itself;
 * {@link dismissRowInlinePadding} owns that correction for every dismissible surface in the app
 * (kitecorp/transferify-ui#1599), and `theme/dismissRowInset.test.ts` pins its arithmetic.
 *
 * That the two distances really do come out equal is measured on a rendered page — this repo tests no
 * CSS declarations, and reading the padding back would only repeat the expression below. See
 * `e2e/post-signup-offer-callout.spec.ts`.
 */
const rootInlinePaddingPx = dismissRowInlinePadding(14);

/** MUI's `fontSize="small"` icon glyph box — the other MUI fact this layout leans on. */
const SMALL_ICON_PX = 20;

/**
 * The dismiss IconButton's OUTER box: the small icon plus MUI's own padding each side — derived from
 * the same shared constant {@link dismissRowInlinePadding} corrects with (kitecorp/transferify-ui#1599),
 * never a fresh literal. Reserved as Root padding so the pinned X owns a strip no flowing child can
 * enter.
 */
const DISMISS_BUTTON_BOX_PX = SMALL_ICON_PX + 2 * SMALL_ICON_BUTTON_PADDING_PX;

/**
 * The post-signup offer callout (kitecorp/transferify-ui#1448). Externalized per the repo's styling
 * convention so the component file stays markup + behavior.
 *
 * <b>GOLD, and deliberately not blue.</b> It shares the files panel's notices slot with
 * {@link ../emailVerification/VerifyEmailCallout}, which owns blue for "something needs your
 * attention" and teal for "the mail is out". An offer is neither: painting it blue would make two
 * unrelated callouts read as one stack of tasks, and the reader would have no way to tell the one
 * that wants something from the one that is giving something away. Gold is already this app's
 * paid-tier accent — the pro pill, the plan badge, the upgrade nudge — so the tone carries the
 * meaning with no label to translate.
 *
 * The 12px radius and the 12px/14px padding are the slot's box, matching the verify callout beside
 * it: a sibling that did not match would read as a different kind of thing. The Root's
 * padding-RIGHT is the one deliberate departure, and it exists to preserve that match rather than break
 * it - see the comment on the rule itself.
 */
export const Root = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px 12px;
	/*
	 * ASYMMETRIC, and smaller on the right on purpose - the same correction PastDueBanner's Root makes,
	 * from the same helper since kitecorp/transferify-ui#1599. The container gives back exactly the
	 * padding the dismiss IconButton carries inside itself, so the X's icon lands as far from the right
	 * edge as the copy sits from the left. The rejected alternative was a negative margin on the button,
	 * which drags the whole control - its hit area and its hover circle included - out of the row rather
	 * than correcting the box it sits in, and which the repo's no-magic-numbers-or-hacks rule forbids for
	 * that reason.
	 */
	position: relative;
	/*
	 * The RIGHT padding reserves the pinned dismiss X's whole column - the icon button's outer box -
	 * so neither the copy nor the Claim button can ever render under it. The X itself sits absolutely
	 * in that reserved strip (see DismissButton): as a flex child it wrapped to a new row whenever the
	 * copy plus the button filled the line, landing bottom-left under the text (kitecorp/transferify-ui#1616,
	 * owner review) - the opposite corner from where a close control belongs.
	 */
	padding: 12px ${rootInlinePaddingPx.right + DISMISS_BUTTON_BOX_PX}px 12px ${rootInlinePaddingPx.left}px;
	border-radius: 12px;
	/*
	 * A REAL edge since ui#1655 (owner, 2026-08-29). The border used to be the same goldTint as the
	 * fill, on the tint-on-tint idiom UpgradeHints uses - which on a translucent wash means the strip
	 * has no boundary of its own and simply fades into the panel. The wash and its edge are shared with
	 * every other full-treatment offer surface since ui#1653; goldOfferWash carries the reasoning.
	 */
	${goldOfferWash};
`;

/**
 * The copy column. The 12rem BASIS is what makes the row reflow rather than squeeze at 375px: the
 * sentence keeps a readable measure and the controls wrap beneath it. A basis rather than a min-width
 * for the reason spelled out on the verify callout's twin — at a 24px root font 12rem exceeds the
 * usable width inside the panel on the one viewport this layout exists to serve.
 */
export const Copy = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	flex: 1 1 12rem;
	min-width: 0;
`;

export const Message = styled(Typography)`
	color: ${({theme}) => theme.tokens.ink};
	font-weight: 700;
`;

/** The supporting line: one step quieter than the offer itself. */
export const Detail = styled(Typography)`
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * FILLED, unlike the verify callout's outlined control — and that difference is the point. This is
 * the one thing on the panel we are asking the reader to do that they did not come here for, and an
 * outlined control beside it would read as the quieter of two equals.
 */
export const ClaimButton = styled(Button)`
	/*
	 * Pushed to the far right and centred against the copy (owner, 2026-08-29). The auto margin is what
	 * does the work on a WRAPPED row: once the button drops to a line of its own at narrow widths it
	 * would otherwise sit hard left, under the sentence. align-self restates the vertical centring the
	 * Root already sets, so the button keeps it if that row rule ever changes.
	 */
	margin-left: auto;
	align-self: center;
	border-radius: ${radii.control};
	white-space: nowrap;
	font-weight: 700;
	${goldClaimFill};
`;

/**
 * Closing the offer. An icon button rather than a text link because the row already carries a text
 * CTA, and two side-by-side text controls would make declining look as weighty as accepting.
 */
export const DismissButton = styled(IconButton)`
	/*
	 * PINNED to the top-right corner, out of the flex flow (kitecorp/transferify-ui#1616): as the row's
	 * last flex child it wrapped below the text at narrow widths. Top offset is the Root's own top
	 * padding minus the button's internal padding, so the ICON's top edge aligns with the copy's first
	 * line; the right offset is the corrected inline padding the Root already computes.
	 */
	position: absolute;
	top: ${12 - SMALL_ICON_BUTTON_PADDING_PX}px;
	right: ${rootInlinePaddingPx.right}px;
	color: ${({theme}) => theme.tokens.ink2};

	&:hover {
		color: ${({theme}) => theme.tokens.ink};
		background-color: ${({theme}) => theme.tokens.goldTint};
	}
`;
